import { useTranslation } from "react-i18next";
import { SubmitHandler } from "react-hook-form";
import { GetProjectResponse } from "api/generatedApi";
import { usePostApiProjectsByProjectIdProjectinformationandtypeMutation } from "api/generatedApi";
import { useDialog } from "shared/dialog/use-dialog";
import { useToast } from "shared/toast/hooks/use-toast";
import { EditProjectTypeAndInfoDialog } from "../edit-project-type-and-info-dialog";
import { ResultDialogType, useMutation } from "utils/hooks/use-mutation";
import { ProjectSetupFormData } from "../../project-setup-form-data";
import { useDashboardRestrictions } from "shared/user-restrictions/use-dashboard-restrictions";
import { formatDateToRequestShortDate } from "utils/formats";

interface Props {
  project: GetProjectResponse;
}
export function useEditProjectTypeAndInfo(props: Props) {
  const { project } = props;
  const { t } = useTranslation();
  const toast = useToast();
  const { canEditProjectSetupProjectType } = useDashboardRestrictions(project);
  const { canEditProjectSetupProjectName } = useDashboardRestrictions(project);

  const [openEditDialog, closeEditDialog] = useDialog(EditProjectTypeAndInfoDialog);
  const [updateProjectInfo] = usePostApiProjectsByProjectIdProjectinformationandtypeMutation();

  const wrapMutation = useMutation({
    onSuccess: closeEditDialog,
    successProps: {
      title: t("project.editProjectType.success.title"),
    },
    errorProps: {
      description: t("project.editProjectType.error.description"),
    },
    resultDialogType: ResultDialogType.Toast,
  });

  const handleOnSubmit: SubmitHandler<ProjectSetupFormData> = async (data) => {
    if (!canEditProjectSetupProjectType() || !canEditProjectSetupProjectName()) {
      toast.error(t("project.editProjectType.restrictedError"));
      return;
    }
    await wrapMutation(
      updateProjectInfo({
        projectId: project.id ?? "",
        updateProjectInformationAndTypeRequest: {
          name: data.title,
          orderNumber: data.orderNumber,
          pieceworkNumber: data.pieceworkNumber,
          pieceWorkSum: data.pieceworkType === "TwelveTwo" ? data.lumpSum : undefined,
          pieceworkType: data.pieceworkType,
          startDate: formatDateToRequestShortDate(data.projectDateStart),
          endDate: formatDateToRequestShortDate(data.projectDateEnd),
        },
      })
    );
  };

  const handleClick = () => {
    openEditDialog({ onSubmit: handleOnSubmit, project });
  };

  return handleClick;
}
